import React, { useContext, useEffect, useRef, useState } from "react";
import "./MemberCreate.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MemberContext } from "../Contexts/MemberContext";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { sendRegistrationEmail } from "./EmailComponents/RegistEmailSender";

const MemberCreate = () => {
  const navigate = useNavigate();
  const { setMembersList, membersList, currentUserInfo } =
    useContext(MemberContext);
  const location = useLocation();
  const { emptyCollection, authorId, returnsub, returnmember } =
    location.state || {};
  const [toEmail, setToEmail] = useState("");
  const { id } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [member, setMember] = useState({
    username: "",
    accountname: "",
    email: "",
    address: "",
    tel_num: "",
    administrator: false,
    admin: false,
    author: { username: "", id: "" },
    profile: "",
    tennisExperience: "",
    racquetUsed: "",
    favoriteShot: "",
    strengthsWeaknesses: "",
    playerStyle: "",
    tennisPhilosophy: "",
    hobbiesSkills: "",
    struggles: "",
  });
  const initialMember = useRef(null);
  const [warningMessage, setWarningMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [prevMemberAdmin, setPrevMemberAdmin] = useState(false);
  const [alertShown, setAlertShown] = useState(false); // ★追加：アラート一回制御

  // メンバー初期化
  useEffect(() => {
    if (authorId) {
      const memberDoc = membersList.find(
        (member) => member.author.id === authorId
      );
      if (returnsub) {
        setMember(returnmember);
      } else {
        if (memberDoc) {
          setMember({ ...memberDoc });
          initialMember.current = { ...memberDoc };
          setIsEditing(true);
        } else {
          console.log("メンバーが見つかりませんでした");
        }
      }
    } else {
      if (returnsub) setMember(returnmember);
      else setCanEdit(!canEdit);
    }
  }, [authorId, membersList]);

  useEffect(() => {
    if (initialMember !== null) {
      setPrevMemberAdmin(initialMember.current?.admin ?? false);
    }
  }, [member]);

  const canShowEditOptionAdmin = currentUserInfo?.administrator === true;

  // 入力処理
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setMember((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));

    // テキストエリア自動高さ
    if (e.target.tagName === "TEXTAREA") {
      e.target.style.height = "auto";
      e.target.style.height = `${e.target.scrollHeight}px`;
    }

    // アカウント名チェック
    if (name === "accountname") {
      if (membersList.find((member) => member.accountname === value)) {
        alert("そのアカウント名は既にありますので変更をお願いします");
      }

      // ★アラート重複防止
      if (value.length > 5 && !alertShown) {
        alert("アカウント名は5文字まででお願いします");
        setAlertShown(true);
      } else if (value.length <= 5 && alertShown) {
        setAlertShown(false);
      }
    }

    // メール形式チェック
    if (name === "email") {
      validateEmail(value);
    }
  };

  // email形式チェック
  const validateEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(email)) {
      setEmailError(false);
      return;
    } else {
      setEmailError("");
    }
  };

  // 次へ
  const nextStep = (e) => {
    e.preventDefault();
    if (emailError === false) {
      alert("メールアドレスが有効なものではありません。");
      setCurrentStep(0);
    }

    if (!isStepValid()) {
      setWarningMessage("すべての項目に入力してください。");
    } else {
      setWarningMessage("");
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const prevStep = () => setCurrentStep((prevStep) => prevStep - 1);

  // 保存処理
  const handleSubmit = async (e) => {
    e.preventDefault();
    const user = auth.currentUser;
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (!user) {
      alert("ログインしてから入力してください。");
      setError("ログインしてから入力してください。");
      return;
    }

    if (!isStepValid()) {
      setWarningMessage("必須項目を入力してください。");
    } else if (emailError) {
      setWarningMessage("有効なメールアドレスを入力してください。");
    } else {
      setWarningMessage("");
    }

    let updatedMember = {};
    try {
      if (isEditing) {
        updatedMember = { ...member };
        if (!updatedMember.id) throw new Error("Member ID is missing");
        await updateDoc(doc(db, "members", updatedMember.id), updatedMember);
        navigate("/memberlist");
      } else {
        const userId = user.uid;
        const username = user.displayName;
        updatedMember = {
          ...member,
          author: { id: userId, username },
        };
        await addDoc(collection(db, "members"), updatedMember);
      }

      const updatedMembers = await getDocs(collection(db, "members"));
      const members = updatedMembers.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMembersList(members);
      setError("");
      navigate("/memberlist");
    } catch (error) {
      console.error("Error adding document: ", error);
      setError(`エラー: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // stepごとの必須チェック
  const isStepValid = () => {
    const currentFields = steps[currentStep - 1].fields;
    return currentFields.every((field) => {
      const value = member[field.id];
      if (field.type === "checkbox") return value !== undefined;
      return value?.trim() !== "";
    });
  };

  // ステップ構成（すべて保持）
  const presteps = [
    {
      id: 1,
      title: "基本情報",
      fields: [
        {
          id: "username",
          label: "お名前",
          type: "text",
          required: true,
          placeholder: "お名前を入力してください",
        },
        {
          id: "accountname",
          label: "アカウント名",
          type: "text",
          required: true,
          placeholder: "アカウント名を入力してください",
        },
        {
          id: "email",
          label: "メールアドレス",
          type: "email",
          required: true,
          placeholder: "メールアドレスを入力してください",
        },
        {
          id: "tel_num",
          label: "携帯電話番号",
          type: "text",
          required: true,
          placeholder: "携帯電話番号を入力してください",
        },
        { id: "admin", label: "参加承認", type: "checkbox" },
        { id: "administrator", label: "管理者権限", type: "checkbox" },
      ],
    },
    {
      id: 2,
      title: "テニスに関する情報 その１",
      fields: [
        {
          id: "tennisExperience",
          label: "テニス歴",
          type: "textarea",
          required: true,
          placeholder: "主なテニス歴をご記入ください",
        },
        {
          id: "racquetUsed",
          label: "使用してきたラケット等",
          type: "textarea",
          required: true,
          placeholder: "使用したラケットや用具について教えてください",
        },
      ],
    },
    {
      id: 3,
      title: "テニスに関する情報 その２",
      fields: [
        {
          id: "favoriteShot",
          label: "好きなショットや得意なショット",
          type: "textarea",
          required: true,
          placeholder: "好きなショットや得意なショットを教えてください",
        },
        {
          id: "strengthsWeaknesses",
          label: "テニスの課題",
          type: "textarea",
          required: true,
          placeholder: "自分の課題や弱点など",
        },
      ],
    },
    {
      id: 4,
      title: "テニスに関する情報 その３",
      fields: [
        {
          id: "playerStyle",
          label: "好きな選手やプレースタイル",
          type: "textarea",
          required: true,
          placeholder: "好きな選手やプレースタイル",
        },
        {
          id: "mainAchievements",
          label: "主な戦績",
          type: "textarea",
          required: true,
          placeholder: "戦績など",
        },
      ],
    },
    {
      id: 5,
      title: "趣味や特技、こだわり",
      fields: [
        {
          id: "hobbiesSkills",
          label: "テニス以外の趣味や特技",
          type: "textarea",
          required: true,
          placeholder: "趣味や特技を教えてください",
        },
        {
          id: "struggles",
          label: "テニス以外のこだわり",
          type: "textarea",
          required: true,
          placeholder: "こだわりを教えてください",
        },
      ],
    },
  ];

  const nonMember = member.author.id.includes("nonMember");

  const steps = !canShowEditOptionAdmin
    ? [
        presteps[0]
          ? {
              ...presteps[0],
              fields: presteps[0].fields
                ? presteps[0].fields
                    .slice(0, 4)
                    .concat(presteps[0].fields.slice(6))
                : [],
            }
          : null,
        ...presteps.slice(1),
      ].filter(Boolean)
    : [...presteps].filter(Boolean);

  const handleConfirm = (e) => {
    e.preventDefault();
    navigate(`/memberdetailconfirm`, {
      state: { member, prevMemberAdmin, authorId, id },
    });
  };

  return (
    <div className="member_detial_container">
      <div className="member_detail_content">
        <form onSubmit={handleConfirm}>
          {steps[currentStep - 1] && (
            <>
              <h2>{steps[currentStep - 1].title}</h2>
              {steps[currentStep - 1].fields.map((field) => (
                <div key={field.id}>
                  <label htmlFor={field.id}>{field.label}</label>
                  {field.type === "textarea" ? (
                    <textarea
                      id={field.id}
                      name={field.id}
                      value={member[field.id] || ""}
                      onChange={handleChange}
                      required={field.required}
                      placeholder={field.placeholder}
                    />
                  ) : field.type === "checkbox" ? (
                    <input
                      type="checkbox"
                      id={field.id}
                      name={field.id}
                      checked={!!member[field.id]}
                      onChange={(e) =>
                        handleChange({
                          target: { name: field.id, value: e.target.checked },
                        })
                      }
                    />
                  ) : (
                    <input
                      type={field.type}
                      id={field.id}
                      name={field.id}
                      value={member[field.id] || ""}
                      onChange={handleChange}
                      maxLength={field.id === "accountname" ? 5 : undefined} // ★5文字制限
                      required={field.required}
                      placeholder={field.placeholder}
                    />
                  )}
                </div>
              ))}
              {emailError && <p className="error-message">{emailError}</p>}
              {warningMessage && (
                <p className="member_detail_massage">{warningMessage}</p>
              )}
              <div className="button-container">
                {currentStep > 1 && (
                  <button type="button" onClick={prevStep}>
                    戻る
                  </button>
                )}
                {currentStep < steps.length ? (
                  <button type="button" onClick={nextStep}>
                    次へ
                  </button>
                ) : (
                  <button type="submit">登録内容を確認</button>
                )}
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default MemberCreate;
